/**
 * TrialBanner — discreet info / warning / expired banner shown on the
 * dashboard when the user is on a 15-day invited trial.
 *
 * Hidden entirely when status === 'none' (existing users, direct signups).
 * Tone shifts from blue (info) to amber (warning) to pink (expired).
 */
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Clock, AlertCircle, Sparkles } from 'lucide-react';
import { useTrialStatus } from '@/hooks/useTrialStatus';

export function TrialBanner() {
  const { t } = useTranslation();
  const { data, isLoading } = useTrialStatus();

  if (isLoading || !data || data.status === 'none') return null;

  const isExpired = data.status === 'expired';
  const isEndingSoon = data.status === 'ending_soon';

  const palette = isExpired
    ? { border: 'border-pink/40', bg: 'from-pink/[0.08] to-violet/[0.05]', icon: 'text-pink', accent: '#ec4899' }
    : isEndingSoon
    ? { border: 'border-amber-300/60', bg: 'from-amber-50 to-pink/[0.04]', icon: 'text-amber-600', accent: '#f59e0b' }
    : { border: 'border-blue-300/40', bg: 'from-blue-50 to-violet/[0.04]', icon: 'text-blue-600', accent: '#3b82f6' };

  const Icon = isExpired ? AlertCircle : isEndingSoon ? Clock : Sparkles;

  const titleKey = isExpired
    ? 'trial.expired.title'
    : isEndingSoon
    ? 'trial.endingSoon.title'
    : 'trial.active.title';
  const subKey = isExpired
    ? 'trial.expired.subtitle'
    : isEndingSoon
    ? 'trial.endingSoon.subtitle'
    : 'trial.active.subtitle';

  const days = data.days_left ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`mb-5 flex items-start gap-3 rounded-2xl border ${palette.border} bg-gradient-to-br ${palette.bg} px-4 py-3 sm:items-center`}
      role="status"
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
        style={{ background: `${palette.accent}1a` }}
      >
        <Icon size={17} className={palette.icon} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[13.5px] font-800 leading-tight tracking-[-0.01em] text-ink">
          {t(titleKey, { count: Math.max(days, 0) })}
        </p>
        <p className="mt-0.5 text-[12px] leading-[1.45] text-ink/65">
          {t(subKey)}
        </p>
      </div>
    </motion.div>
  );
}
